import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { IAssignment } from "Shared/Types/assignment";
import {
  addAssignment,
  deleteAssignment,
  getAssignment,
  getAssignmentsList,
  updateAssignment,
} from "./reducer";

interface BlockTypeSliceState {
  error?: string | null;
  loading: boolean;
  success?: boolean | null;
  assignmentsList: IAssignment[];
  currentAssignment: IAssignment | null;
  isFuncLoading: boolean;
  isLoadingForDelete: boolean;
}

const initialState: BlockTypeSliceState = {
  loading: false,
  error: null,
  assignmentsList: [],
  currentAssignment: null,
  isFuncLoading: false,
  isLoadingForDelete: false,
};

const AssignmentSlice = createSlice({
  name: "AssignmentSlice",
  initialState,
  reducers: {
    clear(state) {
      state.error = null;
      state.loading = false;
      state.success = null;
    },
    clearCurrentAssignment(state) {
      state.currentAssignment = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAssignmentsList.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.assignmentsList = action.payload.results;
    });
    builder.addCase(getAssignment.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.currentAssignment = action.payload;
    });
    builder.addCase(addAssignment.pending, (state) => {
      state.isFuncLoading = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(addAssignment.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.error = null;
      state.success = true;
    });

    builder.addCase(addAssignment.rejected, (state, action) => {
      state.isFuncLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(updateAssignment.pending, (state) => {
      state.isFuncLoading = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(updateAssignment.fulfilled, (state) => {
      state.isFuncLoading = false;
      state.error = null;
      state.success = true;
    });

    builder.addCase(updateAssignment.rejected, (state, action) => {
      state.isFuncLoading = false;
      state.error = action.error.message;
    });

    builder.addCase(deleteAssignment.pending, (state) => {
      state.isLoadingForDelete = true;
      state.error = null;
      state.success = false;
    });

    builder.addCase(deleteAssignment.fulfilled, (state) => {
      state.isLoadingForDelete = false;
      state.error = null;
      state.success = true;
    });

    builder.addCase(deleteAssignment.rejected, (state, action) => {
      state.isLoadingForDelete = false;
      state.error = action.error.message;
    });
  },
});

export const { clear, clearCurrentAssignment } = AssignmentSlice.actions;

export const selectAssignmentsList = (state: RootState) =>
  state.assignment.assignmentsList;
export const selectCurrentAssignment = (state: RootState) =>
  state.assignment.currentAssignment;
export const selectIsLoadingForDelete = (state: RootState) =>
  state.assignment.isLoadingForDelete;
export const selectIsFuncLoading = (state: RootState) =>
  state.assignment.isFuncLoading;
export const selectAssignmentLoading = (state: RootState) =>
  state.assignment.loading;
export const selectAssignmentError = (state: RootState) =>
  state.assignment.error;
export const selectAssignmentSuccess = (state: RootState) =>
  state.assignment.success;

export default AssignmentSlice;
